import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);

// Default timings (override da /security-settings)
const DEFAULT_IDLE_MS = 5 * 60 * 1000;
const DEFAULT_BG_MS = 15 * 1000;
const SESSION_KEY = "relatio_session";
const ACTIVE_KEY = "relatio_last_active";

const readSession = () => { try { return !!sessionStorage.getItem(SESSION_KEY); } catch { return false; } };
const setSession = () => { try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {} };
const clearSession = () => { try { sessionStorage.removeItem(SESSION_KEY); } catch {} };
const touchActive = () => { try { localStorage.setItem(ACTIVE_KEY, String(Date.now())); } catch {} };
const lastActive = () => { try { return Number(localStorage.getItem(ACTIVE_KEY) || 0); } catch { return 0; } };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauth, obj = auth
  const [locked, setLocked] = useState(false);
  const [securityCfg, setSecurityCfg] = useState({ idleMinutes: 5, backgroundSeconds: 15 });
  const idleRef = useRef(null);
  const idleMsRef = useRef(DEFAULT_IDLE_MS);
  const bgMsRef = useRef(DEFAULT_BG_MS);
  const userRef = useRef(undefined);
  userRef.current = user;

  const loadSecurity = useCallback(async () => {
    try {
      const { data } = await api.get("/security-settings");
      setSecurityCfg(data);
      idleMsRef.current = Math.max(60000, (data.idleMinutes || 5) * 60 * 1000);
      bgMsRef.current = Math.max(0, (data.backgroundSeconds || 15) * 1000);
    } catch {}
  }, []);

  const lockScreen = useCallback(() => { if (userRef.current) setLocked(true); }, []);

  const logout = useCallback(async () => {
    clearSession();
    try { await api.post("/auth/logout"); } catch {}
    setLocked(false);
    setUser(null);
  }, []);

  // Gate iniziale
  useEffect(() => {
    (async () => {
      const hasSession = readSession();
      const away = Date.now() - lastActive();
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadSecurity();
        const bg = bgMsRef.current;
        if (!hasSession || (lastActive() && away > bg)) {
          setLocked(true); // app riaperta da chiusa o dopo background: richiede PIN/password
        } else {
          setLocked(false);
          setSession();
          touchActive();
        }
      } catch {
        setUser(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Attività, visibilità, inattività
  useEffect(() => {
    const armIdle = () => {
      if (idleRef.current) clearTimeout(idleRef.current);
      idleRef.current = setTimeout(() => lockScreen(), idleMsRef.current);
    };
    const onActivity = () => {
      if (userRef.current && !locked) { touchActive(); armIdle(); }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        touchActive();
      } else {
        const away = Date.now() - lastActive();
        if (userRef.current && !locked && lastActive() && away > bgMsRef.current) lockScreen();
        else onActivity();
      }
    };
    const events = ["pointerdown", "keydown", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    if (user && !locked) armIdle();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      if (idleRef.current) clearTimeout(idleRef.current);
    };
  }, [user, locked, lockScreen]);

  const afterAuth = (data) => {
    setSession();
    touchActive();
    setLocked(false);
    setUser(data);
    loadSecurity();
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    afterAuth(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    afterAuth(data);
    return data;
  };

  const verifyPin = async (pin) => {
    await api.post("/auth/verify-pin", { pin });
    setSession();
    touchActive();
    setLocked(false);
  };

  const refreshMe = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; } catch { return null; }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, locked, setLocked, login, register, logout, verifyPin, refreshMe, securityCfg, loadSecurity }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
